import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { useSubscriptionLimits } from "../../hooks/useSubscriptionLimits";
import { useAccessControl } from "../../hooks/useAccessControl";
import { getInstructorStats, shouldRefreshStats, refreshInstructorStats, InstructorStats } from "../../services/instructorStatsService";
import { getCached, setCached } from "../../utils/sessionCache";
import SubdomainDashboard from "./SubdomainDashboard";
import EmailVerificationBanner from "../../components/EmailVerificationBanner";
import SubscriptionStatusBanner from "../../components/SubscriptionStatusBanner";
import TrialExpirationWarning from "../../components/TrialExpirationWarning";
import SubscriptionExpiredBanner from "../../components/SubscriptionExpiredBanner";
import ModeratorManagement from "../../components/ModeratorManagement";
import DashboardSidebar from "../../components/DashboardSidebar";
import DashboardMobileDropdown from "../../components/DashboardMobileDropdown";
import DashboardContent from "../../components/DashboardContent";
import AccessBlockBanner from "../../components/AccessBlockBanner";
import Navbar from "../../components/Navbar";
import { Crown, Sparkles } from "lucide-react";

export default function InstructorDashboard() {
  const { user, loading: authLoading, emailVerified } = useAuthContext();
  const { plan } = useSubscription();
  const { usage, loading: limitsLoading, userTier, getVideoStorageInfo, getFileStorageInfo } = useSubscriptionLimits();
  const { hasAccess, blockReason, blockMessage, isLoading: accessLoading } = useAccessControl();
  const navigate = useNavigate();
  const [stats, setStats] = useState<InstructorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [activeSection, setActiveSection] = useState('overview');
  
  const isPro = userTier === 'pro_monthly' || userTier === 'pro_yearly' || userTier === 'pro';
  const videoInfo = getVideoStorageInfo();
  const fileInfo = getFileStorageInfo();
  
  // Check Salepage access: Pro+ Yearly or admin-enabled
  const hasSalePageAccess = 
    userTier === 'pro_plus_yearly' || 
    ((user as any)?.salePageSubscription?.status === 'active');

  // Redirect if not authenticated or not instructor
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!authLoading && user?.role !== "instructor" && user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  // Expired users can see dashboard but features will be disabled
  // Free tier users can also see dashboard but will see upgrade prompts

  useEffect(() => {
    async function loadStats() {
      if (!user) return;

      try {
        setLoading(true);

        // Cache-first: Check if we have cached stats
        const cacheKey = `instructor_stats_${user.uid}`;
        const cachedStats = getCached<InstructorStats>(cacheKey);
        
        if (cachedStats) {
          // Serve cached data immediately
          setStats(cachedStats);
          setLastUpdated(formatLastUpdated(cachedStats.updatedAt.toDate()));
          setLoading(false);
          
          // Check if refresh needed in background
          const needsRefresh = await shouldRefreshStats(user.uid);
          if (needsRefresh) {
            // Refresh in background, don't block UI
            refreshInstructorStats(user.uid)
              .then(newStats => {
                setStats(newStats);
                setLastUpdated(formatLastUpdated(newStats.updatedAt.toDate()));
                setCached(cacheKey, newStats);
              })
              .catch(console.error);
          }
        } else {
          // No cache - load from aggregated stats
          const instructorStats = await getInstructorStats(user.uid);
          if (instructorStats) {
            setStats(instructorStats);
            setLastUpdated(formatLastUpdated(instructorStats.updatedAt.toDate()));
            setCached(cacheKey, instructorStats);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error("Error loading dashboard stats:", error);
        setLoading(false);
      }
    }

    loadStats();
  }, [user]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  function formatLastUpdated(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'เมื่อสักครู่';
    if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
    
    return date.toLocaleDateString('th-TH', { 
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  // Show loading state
  if (loading || authLoading || limitsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar transparent={false} />
      <div className="min-h-screen bg-grayBG px-4 pt-4 pb-20">
        {/* Header */}
        <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 md:gap-4 mb-2">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-textDark">Dashboard</h1>
            <p className="text-xs md:text-sm text-textLight mt-0.5 md:mt-1">
              ภาพรวมคอร์สของคุณ
              {lastUpdated && (
                <span className="ml-2 text-[10px] md:text-xs opacity-75">• อัปเดตล่าสุด {lastUpdated}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Access Block Banner - Shows when user doesn't have access */}
      {!hasAccess && !accessLoading && (
        <AccessBlockBanner blockReason={blockReason} blockMessage={blockMessage} />
      )}

      {/* Pro Premium Badge */}
      {isPro && (
        <div className="mb-4 md:mb-6 lg:mb-8 bg-gradient-to-r from-blue-500 via-pink-500 to-blue-600 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 text-white shadow-2xl">
          <div className="flex items-center justify-between flex-wrap gap-2 md:gap-3 lg:gap-4">
            <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Crown className="w-5 h-5 md:w-6 md:h-6 lg:w-8 lg:h-8" />
              </div>
              <div>
                <h2 className="text-base md:text-lg lg:text-2xl font-bold flex items-center gap-1 md:gap-2">
                  PRO Member
                  <Sparkles className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                </h2>
                <p className="text-white/90 text-[10px] md:text-xs lg:text-sm mt-0.5 md:mt-1">แพ็กเกจเต็มรูปแบบพร้อมฟีเจอร์ครบครัน</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <div className="text-right">
                <div className="text-[10px] md:text-xs lg:text-sm text-white/80">SLA Support</div>
                <div className="text-sm md:text-base lg:text-lg font-bold">99.9% Uptime</div>
              </div>
              <div className="w-7 h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-white/20 backdrop-blur-sm p-2 md:p-2.5 lg:p-3 rounded-lg md:rounded-xl">
                <svg className="w-full h-full text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pro Subdomain Dashboard */}
      {isPro && user?.username && (
        <div className="mb-4 md:mb-6 lg:mb-8">
          <SubdomainDashboard />
        </div>
      )}

      {/* Main Dashboard Layout */}
      <div className="flex flex-col lg:flex-row gap-3 md:gap-4 lg:gap-6">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block">
          <DashboardSidebar
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            isPro={isPro}
            hasAccess={hasAccess}
          />
        </div>

        {/* Mobile Dropdown */}
        <div className="lg:hidden w-full">
          <DashboardMobileDropdown
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
            isPro={isPro}
            hasAccess={hasAccess}
          />
        </div>

        {/* Content Area */}
        <div className="flex-1">
          <DashboardContent 
            activeSection={activeSection}
            stats={stats}
            user={user}
            plan={plan}
            videoInfo={videoInfo}
            fileInfo={fileInfo}
            isPro={isPro}
            lastUpdated={lastUpdated}
            emailVerified={user?.emailVerified}
            onSectionChange={handleSectionChange}
            hasAccess={hasAccess}
            blockMessage={blockMessage}
            blockReason={blockReason}
          />
        </div>
      </div>
    </div>
    </>
  );
}
